/**
 * Escrever ou corrigir um item da ata — e o lugar onde a fronteira é decidida por uma pessoa.
 *
 * **O momento em que o erro caro é cometido é este:** alguém digita uma frase franca sobre o
 * cliente numa caixa de texto. A tela responde **enquanto ele digita** quem vai ler aquilo — a
 * marca de destino fica junto do campo, a moldura inteira muda de cor com a escolha, e as duas
 * coisas acompanham o tipo nos dois sentidos. Uma tela que continuasse dizendo "vai ao cliente"
 * depois de o tipo virar nota interna seria pior do que não dizer nada.
 *
 * **O destino não é uma caixinha de marcar.** Um `checkbox` chamado "visível ao cliente" tem um
 * estado sem frase nenhuma — o desmarcado —, e o desmarcado é justamente o que ninguém confere.
 * São duas opções do mesmo tamanho, com a consequência escrita em cada uma, e nenhuma delas é o
 * silêncio.
 *
 * **Nota interna trava as duas.** Não é enfeite: o `CHECK`
 * `ck_ata_item_nota_interna_nunca_visivel` torna a linha impossível no banco, e o servidor recusa
 * com `422`. Um controle clicável ali ofereceria exatamente o clique que produz o erro caro. O
 * que sai para o servidor é `visivel_ao_cliente: false` explícito — o destino calculado do item
 * que está sendo salvo, e não o resíduo de uma escolha feita no meio do caminho.
 *
 * **O tipo que a sessão não pode escrever não entra na lista.** Sem `ata:ver_interno`, "nota
 * interna" não aparece nem como opção desabilitada: ela não existe. Quem não enxerga a nota não
 * pode criá-la às cegas nem converter nela um item que o cliente já leu.
 *
 * **A correção é parcial no fio, e completa aqui.** O servidor aplica só o que veio no corpo
 * (`exclude_unset`), e este formulário envia todos os campos que ele governa — inclusive os
 * `null` de responsável e prazo quando o tipo deixa de ser pendência. Mandar menos deixaria um
 * prazo órfão pendurado num item que não é mais cobrança.
 */

import { useState } from "react";
import { Aviso, Botao, Campo, CampoLongo, GrupoDeOpcoes, Opcao, Segmentado } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  LADOS_DO_RESPONSAVEL,
  MAXIMO_DO_RESPONSAVEL,
  MAXIMO_DO_TEXTO_DO_ITEM,
  MINUTO_MAXIMO_DO_ITEM,
  ROTULO_DO_LADO,
  ROTULO_DO_TIPO_DE_ITEM,
  destinoDoItem,
  useAcrescentarItem,
  useEditarItem,
  type ItemDaAta,
  type LadoDoResponsavel,
  type TipoDeItem,
} from "../../nucleo/reuniao";
import { MarcaDeDestino } from "./MarcaDeDestino";

export interface PropsDoFormularioDoItem {
  ataId: string;
  /** Presente = correção de um item que já existe; ausente = item novo. */
  item?: ItemDaAta | null;
  /** Se a sessão tem `ata:ver_interno` — sem ela o servidor recusa escrever nota interna. */
  podeEscreverNotaInterna: boolean;
  aoFechar: () => void;
}

/* A ordem importa: decisão primeiro porque é o item mais comum de uma ata, e nota interna por
   último porque é o que menos se escreve e o que mais custa errar. */
const TIPOS_OFERECIDOS: ReadonlyArray<{ valor: TipoDeItem; rotulo: string }> = [
  { valor: "decisao", rotulo: ROTULO_DO_TIPO_DE_ITEM.decisao },
  { valor: "pendencia", rotulo: ROTULO_DO_TIPO_DE_ITEM.pendencia },
  { valor: "nota_interna", rotulo: ROTULO_DO_TIPO_DE_ITEM.nota_interna },
];

export function FormularioDoItem({
  ataId,
  item = null,
  podeEscreverNotaInterna,
  aoFechar,
}: PropsDoFormularioDoItem) {
  const [tipo, setTipo] = useState<TipoDeItem>(item?.tipo ?? "decisao");
  const [texto, setTexto] = useState(item?.texto ?? "");
  const [responsavel, setResponsavel] = useState(item?.responsavel ?? "");
  const [lado, setLado] = useState<LadoDoResponsavel | "">(item?.responsavel_lado ?? "");
  const [prazo, setPrazo] = useState(item?.prazo ?? "");
  const [minuto, setMinuto] = useState(item?.minuto === null ? "" : String(item?.minuto ?? ""));
  const [visivel, setVisivel] = useState(item?.visivel_ao_cliente ?? false);

  const acrescentar = useAcrescentarItem(ataId);
  const editar = useEditarItem(ataId);
  const envio = item ? editar : acrescentar;

  /* Ler o destino desta função, e não do estado `visivel` cru, é o que garante que a marca, a
     moldura e o que vai ser gravado nunca discordem: nota interna força o lado de dentro nos
     três lugares de uma vez. */
  const destino = destinoDoItem({ tipo, visivel_ao_cliente: visivel });
  const ehNotaInterna = tipo === "nota_interna";
  const ehPendencia = tipo === "pendencia";
  const faltaLado = ehPendencia && lado === "";
  const escrito = texto.trim().length > 0;
  const erro = envio.isError ? mensagemDoConsole(envio.error) : null;

  /* O tipo que a sessão não pode escrever não entra na lista — não como opção desabilitada, que
     convidaria ao clique que o servidor recusa com 403. */
  const tiposOferecidos = TIPOS_OFERECIDOS.filter(
    (opcao) => opcao.valor !== "nota_interna" || podeEscreverNotaInterna,
  );

  function trocarTipo(novo: TipoDeItem) {
    setTipo(novo);
    /* Lado e prazo só existem em pendência (`MENSAGEM_DE_CAMPO_FORA_DA_PENDENCIA`). O
       `visivel` NÃO é zerado aqui: `destinoDoItem` já o ignora em nota interna, e zerá-lo faria
       a ida e volta de tipo apagar em silêncio uma escolha que a pessoa tinha feito. */
    if (novo !== "pendencia") {
      setLado("");
      setPrazo("");
    }
  }

  function enviar() {
    const corpo = {
      tipo,
      texto: texto.trim(),
      responsavel: responsavel.trim() || null,
      responsavel_lado: ehPendencia && lado !== "" ? lado : null,
      prazo: ehPendencia && prazo ? prazo : null,
      minuto: minuto.trim() === "" ? null : Number(minuto),
      // O destino calculado, nunca a caixa crua: em nota interna ele é `false` por construção.
      visivel_ao_cliente: destino === "cliente",
    };
    const opcoes = { onSuccess: () => aoFechar() };
    if (item) {
      editar.mutate({ itemId: item.id, corpo }, opcoes);
    } else {
      acrescentar.mutate(corpo, opcoes);
    }
  }

  return (
    <div className="formulario-do-item" data-destino={destino} data-testid="formulario-do-item">
      <p className="formulario-do-item__aviso" role="status">
        <MarcaDeDestino destino={destino} data-testid="destino-em-edicao" />{" "}
        {destino === "cliente"
          ? "— este texto sai na ata publicada, com estas palavras."
          : "— O cliente não lê isto. Não aparece para ele na ata, no resumo nem em listagens. Fica registrado só nos comentários internos do GeoTask."}
      </p>

      <Segmentado<TipoDeItem>
        rotulo="Tipo do item"
        itens={tiposOferecidos}
        valor={tipo}
        aoMudar={trocarTipo}
        className="mb-3"
      />

      <CampoLongo
        rotulo={ehNotaInterna ? "A nota interna" : "O que ficou registrado"}
        rows={3}
        value={texto}
        maxLength={MAXIMO_DO_TEXTO_DO_ITEM}
        onChange={(evento) => setTexto(evento.target.value)}
        ajuda={
          tipo === "decisao"
            ? "Uma frase que o cliente vai reler daqui a três meses para lembrar o que foi combinado."
            : tipo === "pendencia"
              ? "O que precisa acontecer, e não o que aconteceu. Quem lê é quem tem a bola."
              : "O que a equipe precisa lembrar e o cliente não precisa ler. Fica dentro de casa."
        }
        data-testid="texto-do-item"
      />

      <div className="grade-do-item">
        <Campo
          rotulo="Responsável (opcional)"
          value={responsavel}
          maxLength={MAXIMO_DO_RESPONSAVEL}
          onChange={(evento) => setResponsavel(evento.target.value)}
          placeholder="Nome de quem ficou com isso"
          data-testid="responsavel-do-item"
        />
        {ehPendencia ? (
          <Campo
            rotulo="Prazo (opcional)"
            type="date"
            value={prazo}
            onChange={(evento) => setPrazo(evento.target.value)}
            data-testid="prazo-do-item"
          />
        ) : null}
        <Campo
          rotulo="Minuto na gravação (opcional)"
          type="number"
          min={0}
          max={MINUTO_MAXIMO_DO_ITEM}
          value={minuto}
          onChange={(evento) => setMinuto(evento.target.value)}
          ajuda="Em minutos, para quem for reler a transcrição achar o trecho."
          data-testid="minuto-do-item"
        />
      </div>

      {ehPendencia ? (
        <GrupoDeOpcoes rotulo="De que lado está o responsável?" className="mt-2">
          {LADOS_DO_RESPONSAVEL.map((valor) => (
            <Opcao
              key={valor}
              tipo="radio"
              name={`lado-${item?.id ?? "novo"}`}
              checked={lado === valor}
              onChange={() => setLado(valor)}
              titulo={ROTULO_DO_LADO[valor]}
              descricao={
                valor === "cliente"
                  ? "Vira cobrança na esteira dele quando a ata for publicada — e só se este item for ao cliente."
                  : "Fica na fila da GEOvendas. O cliente não é cobrado por ela."
              }
              data-testid={`lado-${valor}`}
            />
          ))}
        </GrupoDeOpcoes>
      ) : null}

      {/* A escolha do destino fica DEPOIS do texto de propósito: quem acabou de escrever tem a
          frase fresca na cabeça, e é nesse instante que a pergunta "isto pode sair daqui?" faz
          sentido. Antes de escrever, ela é abstrata. */}
      <GrupoDeOpcoes rotulo="Quem vai ler este item?" className="mt-2">
        <div className="escolha-de-destino">
          <Opcao
            className="opcao--cliente"
            tipo="radio"
            name={`destino-${item?.id ?? "novo"}`}
            checked={destino === "cliente"}
            /* Em nota interna as duas opções ficam DESABILITADAS, e não escondidas: o `CHECK`
               `ck_ata_item_nota_interna_nunca_visivel` torna a linha impossível no banco, e uma
               caixa clicável aqui ofereceria exatamente o clique que produz o erro caro. */
            disabled={ehNotaInterna}
            onChange={() => setVisivel(true)}
            titulo="O cliente vê"
            descricao="Sai na ata publicada, com este texto, para qualquer pessoa da empresa dele que abrir o portal."
            data-testid="destino-cliente"
          />
          <Opcao
            className="opcao--interno"
            tipo="radio"
            name={`destino-${item?.id ?? "novo"}`}
            checked={destino === "interno"}
            disabled={ehNotaInterna}
            onChange={() => setVisivel(false)}
            titulo="Só a GEOvendas vê"
            descricao="Não aparece na ata dele, nem no resumo, nem no resumo de Kick Off. O cliente não vê esta linha."
            data-testid="destino-interno"
          />
        </div>
      </GrupoDeOpcoes>

      {ehNotaInterna ? (
        <Aviso tom="atencao" className="mb-3" data-testid="nota-interna-travada">
          <b>Nota interna nunca vai ao cliente.</b> Se o texto precisa chegar a ele, não é uma nota
          interna — troque o tipo e reescreva com as palavras que você diria a ele.
        </Aviso>
      ) : null}

      {faltaLado ? (
        <Aviso tom="marca" className="mb-3" data-testid="pendencia-sem-lado">
          Toda pendência precisa dizer de que lado está o responsável: é isso que decide se ela
          vira linha na esteira do cliente ou fila interna.
        </Aviso>
      ) : null}

      {erro ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-do-item">
          {erro}
        </Aviso>
      ) : null}

      <div className="acoes-do-console">
        <Botao
          tamanho="pequeno"
          disabled={envio.isPending || !escrito || faltaLado}
          onClick={enviar}
          data-testid="salvar-item"
        >
          {envio.isPending ? "Salvando…" : item ? "Salvar a correção" : "Acrescentar à ata"}
        </Botao>
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          disabled={envio.isPending}
          onClick={aoFechar}
          data-testid="cancelar-item"
        >
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
